/**
 * DEFCAT P6 + R2-06/07/10 — detail panel for the selected tree node.
 *
 * Edits PATCH the entry on the current DRAFT version (read-only when no draft
 * is active). The node editor exposes the full user-meaningful column set:
 *
 *   - name            — i18n row editor; English required (R2-06).
 *   - abbreviation    — i18n row editor; English MAY be empty (R2-07 / OQ-R2-4
 *                       exception); when every row is empty the field PATCHes
 *                       null (no abbreviation at all is valid).
 *   - externalIds     — array {provider, id} replace-list editor (R2-07).
 *   - default severity— TYPE nodes only: a dropdown of the catalogue's class
 *                       model classes, stored as `defaultSeverityByModel`
 *                       keyed by the catalogue's class-model id (R2-07).
 *   - defaultDescription — i18n row editor (R2-06/07; was a single field).
 *
 * Retire / re-enable (R2-10): a retired node (isActive === false) renders a
 * "Retired" chip; the action button swaps Retire ⇄ Re-enable.
 *
 * Read-only metadata (createdAt/by, version) stays OUT of the form (R2-07 AC4).
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { typography } from "@aeriosiq/ui";
import type {
  CatalogueEntryExternalId,
  DefectCatalogueEntryV2,
  DefectClassModel,
  UpdateEntryBody,
} from "@aeriosiq/types";
import { useUpdateEntryMutation } from "./taxonomyQueries.js";
import { locText } from "./locText.js";
import {
  I18nRowEditor,
  i18nMapToRows,
  i18nRowsEmpty,
  i18nRowsToMap,
  type I18nRow,
} from "./I18nRowEditor.js";
import {
  CatalogueExternalIdsEditor,
  cleanExternalIdRows,
} from "./CatalogueExternalIdsEditor.js";

/**
 * Section-heading style matching the analysis filter widget's `LevelHeader`
 * (the platform's canonical compact-widget scale): label-md token, semibold,
 * uppercase, tonal text.secondary. Anything larger reads as oversized here.
 */
const sectionHeadingSx = {
  fontSize: typography.size["label-md"],
  fontWeight: typography.weight.semibold,
  letterSpacing: typography.tracking["label-md"],
  color: "text.secondary",
  textTransform: "uppercase" as const,
};

/** Compact body/value text — caption-lg (13) like the filter widget body. */
const bodyTextSx = {
  fontSize: typography.size["caption-lg"],
};

/**
 * Dense TextField/Select scale: input + label at caption-lg (13), helper at
 * 11 — the filter-widget caption size. Pairs with `size="small"`.
 */
const compactFieldSx = {
  "& .MuiInputBase-input": { fontSize: typography.size["caption-lg"] },
  "& .MuiInputLabel-root": { fontSize: typography.size["caption-lg"] },
  "& .MuiMenuItem-root": { fontSize: typography.size["caption-lg"] },
  "& .MuiFormHelperText-root": { fontSize: 11, mt: 0.25 },
};

export interface EntryDetailPanelProps {
  catalogueId: string;
  versionId: string;
  entry: DefectCatalogueEntryV2;
  /** Candidate parents (same axis, excluding self + descendants). */
  parentOptions: { entryId: string; label: string }[];
  /**
   * DEFCAT-R2-04 AC4 — the resolved parent display name, always shown. For a
   * depth-0 location entry this is its L0 component-group name (the entry has
   * no catalogue parent but conceptually hangs under the L0).
   */
  parentDisplay?: string | null;
  editable: boolean;
  /** The catalogue's class model — drives the TYPE-node severity dropdown. */
  classModel?: DefectClassModel | null | undefined;
}

export function EntryDetailPanel({
  catalogueId,
  versionId,
  entry,
  parentOptions,
  parentDisplay,
  editable,
  classModel,
}: EntryDetailPanelProps): ReactElement {
  const [nameRows, setNameRows] = useState<I18nRow[]>(() =>
    i18nMapToRows(entry.name),
  );
  const [abbrevRows, setAbbrevRows] = useState<I18nRow[]>(() =>
    i18nMapToRows(entry.abbreviation),
  );
  const [descRows, setDescRows] = useState<I18nRow[]>(() =>
    i18nMapToRows(entry.defaultDescription),
  );
  const [externalIds, setExternalIds] = useState<CatalogueEntryExternalId[]>(
    () => entry.externalIds ?? [],
  );
  const [parentId, setParentId] = useState<string>(entry.parentEntryId ?? "");
  const [severityClassId, setSeverityClassId] = useState<string>("");

  const update = useUpdateEntryMutation(catalogueId, versionId);

  const isTypeNode = entry.axis === "type";

  // Default-severity key = the catalogue's class-model id. The stored map is
  // class_model_id → defect_class_id; read this entry's current value for it.
  const classModelId = classModel?.classModelId ?? null;

  useEffect(() => {
    setNameRows(i18nMapToRows(entry.name));
    setAbbrevRows(i18nMapToRows(entry.abbreviation));
    setDescRows(i18nMapToRows(entry.defaultDescription));
    setExternalIds(entry.externalIds ?? []);
    setParentId(entry.parentEntryId ?? "");
    const sev = entry.defaultSeverityByModel ?? {};
    setSeverityClassId(
      (classModelId !== null ? sev[classModelId] : undefined) ?? "",
    );
  }, [entry, classModelId]);

  const retired = entry.isActive === false;

  const enName = nameRows.find((r) => r.lang === "en")?.value.trim() ?? "";
  const nameRequiredMissing = enName.length === 0;

  const classes = useMemo(
    () =>
      [...(classModel?.classes ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      ),
    [classModel],
  );

  function handleSave(): void {
    const body: UpdateEntryBody = {
      name: i18nRowsToMap(nameRows),
      parentEntryId: parentId.length > 0 ? parentId : null,
      // Abbreviation: no abbreviation at all is valid → PATCH null when empty.
      abbreviation: i18nRowsEmpty(abbrevRows)
        ? null
        : i18nRowsToMap(abbrevRows),
      defaultDescription: i18nRowsEmpty(descRows)
        ? null
        : i18nRowsToMap(descRows),
      // External IDs: replace-list — send the cleaned array (empty clears).
      externalIds: cleanExternalIdRows(externalIds),
    };
    // Default severity (TYPE nodes only) — store under the class-model id.
    if (isTypeNode && classModelId !== null) {
      body.defaultSeverityByModel =
        severityClassId.length > 0 ? { [classModelId]: severityClassId } : {};
    }
    update.mutate({ entryId: entry.entryId, body });
  }

  function handleRetireToggle(): void {
    update.mutate({
      entryId: entry.entryId,
      body: { isActive: retired },
    });
  }

  return (
    <Box sx={{ p: 1 }}>
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          rowGap={0.75}
        >
          <Typography component="span" sx={sectionHeadingSx}>
            {entry.axis === "location" ? "Location node" : "Type node"}
            {entry.isLeaf === true ? " · leaf" : ""}
          </Typography>
          {retired ? (
            <Chip
              size="small"
              label="Retired"
              color="default"
              variant="outlined"
              sx={{
                height: 18,
                color: "text.disabled",
                borderColor: "text.disabled",
                "& .MuiChip-label": {
                  px: 0.75,
                  fontSize: 11,
                },
              }}
            />
          ) : null}
        </Stack>
        {!editable ? (
          <Alert
            severity="info"
            sx={{ py: 0, "& .MuiAlert-message": bodyTextSx }}
          >
            This version is published (read-only). Branch a draft from the
            Versions tab to edit.
          </Alert>
        ) : null}
        {update.isError ? (
          <Alert
            severity="error"
            sx={{ py: 0, "& .MuiAlert-message": bodyTextSx }}
          >
            Could not save the entry.
          </Alert>
        ) : null}

        <I18nRowEditor
          label="Name"
          value={nameRows}
          onChange={setNameRows}
          disabled={!editable}
          helperText="English is required; add other languages as needed."
        />

        <I18nRowEditor
          label="Abbreviation"
          value={abbrevRows}
          onChange={setAbbrevRows}
          disabled={!editable}
          helperText="Optional — no abbreviation at all is valid."
        />

        <CatalogueExternalIdsEditor
          value={externalIds}
          onChange={setExternalIds}
          disabled={!editable}
        />

        {isTypeNode ? (
          classModelId !== null && classes.length > 0 ? (
            <Box>
              <Typography
                component="span"
                sx={{ ...sectionHeadingSx, display: "block", mb: 0.75 }}
              >
                Default severity
              </Typography>
              <TextField
                select
                size="small"
                value={severityClassId}
                onChange={(e) => {
                  setSeverityClassId(e.target.value);
                }}
                fullWidth
                disabled={!editable}
                sx={compactFieldSx}
                slotProps={{
                  htmlInput: { "aria-label": "Default severity" },
                  select: {
                    sx: { fontSize: typography.size["caption-lg"] },
                    displayEmpty: true,
                    renderValue: (v: unknown) => {
                      const id = typeof v === "string" ? v : "";
                      if (id.length === 0) return "(none)";
                      const c = classes.find((x) => x.defectClassId === id);
                      if (!c) return id;
                      return `${String(c.classCategory)}${c.valuation ? ` — ${locText(c.valuation, "")}` : ""}`;
                    },
                  },
                }}
                helperText={`Class model: ${locText(classModel?.modelName, "(default)")}`}
              >
                <MenuItem value="" sx={bodyTextSx}>
                  (none)
                </MenuItem>
                {classes.map((c) => (
                  <MenuItem
                    key={c.defectClassId}
                    value={c.defectClassId}
                    sx={bodyTextSx}
                  >
                    {c.classCategory}
                    {c.valuation ? ` — ${locText(c.valuation, "")}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={bodyTextSx}>
              Default severity unavailable — the catalogue&apos;s class model
              has no classes loaded.
            </Typography>
          )
        ) : null}

        <I18nRowEditor
          label="Default description"
          value={descRows}
          onChange={setDescRows}
          disabled={!editable}
          multiline
          helperText="Pre-fills the defect drawer's notes when this type is picked."
        />

        {/* Parent — section heading matches the other (uppercase) sections;
            the ACTUAL parent shows INSIDE the select like every other set
            parameter (Christian, 2026-06-04). For a depth-0 location entry
            the conceptual parent is its L0 component group (parentDisplay) —
            rendered as the select's value for the empty selection. */}
        <Box>
          <Typography
            component="span"
            sx={{ ...sectionHeadingSx, display: "block", mb: 0.75 }}
          >
            Parent
          </Typography>
          <TextField
            select
            size="small"
            value={parentId}
            onChange={(e) => {
              setParentId(e.target.value);
            }}
            fullWidth
            disabled={!editable}
            sx={compactFieldSx}
            slotProps={{
              htmlInput: { "aria-label": "Parent" },
              select: {
                sx: { fontSize: typography.size["caption-lg"] },
                displayEmpty: true,
                renderValue: (v: unknown) => {
                  const id = typeof v === "string" ? v : "";
                  if (id.length === 0) {
                    return parentDisplay != null && parentDisplay.length > 0
                      ? parentDisplay
                      : "(root)";
                  }
                  return (
                    parentOptions.find((p) => p.entryId === id)?.label ?? id
                  );
                },
              },
            }}
            helperText="Move this node under a different parent (or to the root)."
          >
            <MenuItem value="" sx={bodyTextSx}>
              {parentDisplay != null && parentDisplay.length > 0
                ? `(root — under ${parentDisplay})`
                : "(root)"}
            </MenuItem>
            {parentOptions.map((p) => (
              <MenuItem key={p.entryId} value={p.entryId} sx={bodyTextSx}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {entry.axis === "location" && entry.isLeaf === true ? (
          <Typography color="text.secondary" sx={bodyTextSx}>
            Rollup (global location): {entry.globalLocationDetailId ?? "(none)"}{" "}
            · Component group: {entry.componentGroupId ?? "(none)"}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={!editable || update.isPending || nameRequiredMissing}
          >
            Save
          </Button>
          <Button
            size="small"
            color={retired ? "primary" : "error"}
            onClick={handleRetireToggle}
            disabled={!editable || update.isPending}
          >
            {retired ? "Re-enable" : "Retire"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
